import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { ContactModel } from '../../../contact/models/contact.model';
import { ContactRepository } from '../../../contact/states/contact.repository';
import { AnimalModel } from '../../models/animal.model';
import { CARE_OPTIONS, NUTRITION_OPTIONS } from '../../models/body-condition-score.model';
import { AnimalMedicalRecordModel } from '../../models/medical/animal-medical-record.model';
import { AnimalPreventiveTreatmentModel } from '../../models/medical/animal-preventive-treatment.model';
import {
  getMedicalConditionKindLabelKey,
  getMedicalRecordTypeLabelKey,
  getPreventiveTreatmentCategoryLabelKey,
  getPreventiveTreatmentTypeLabelKey,
  MedicalRecordType
} from '../../models/medical/medical.enums';
import { formatVeterinarianName, gramsToKg } from '../../models/medical/medical.helpers';
import { MedicalChangeChip, toChangeChips } from '../../models/medical/medical-record-changes';
import { refId } from '../../models/medical/medical-ref.model';
import { AnimalMedicalRepository } from '../../states/medical/animal-medical.repository';
import { MedicalFile, MedicalRecordPositions } from '../../states/medical/animal-medical.selectors';

const PAGE_SIZE = 10;

@Component({
  selector: 'app-medical-record-list',
  templateUrl: './medical-record-list.component.html',
  styleUrls: ['../medical-shared.scss'],
  standalone: false
})
export class MedicalRecordListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() animal: AnimalModel | null = null;
  @Input() highlightRecordId: number | null = null;
  @Output() create = new EventEmitter<void>();
  @Output() correct = new EventEmitter<AnimalMedicalRecordModel>();
  @Output() openFile = new EventEmitter<MedicalFile>();

  readonly pageSize = PAGE_SIZE;
  protected readonly recordTypeLabelKey = getMedicalRecordTypeLabelKey;
  protected readonly categoryLabelKey = getPreventiveTreatmentCategoryLabelKey;
  protected readonly typeLabelKey = getPreventiveTreatmentTypeLabelKey;
  protected readonly conditionKindLabelKey = getMedicalConditionKindLabelKey;
  protected readonly gramsToKg = gramsToKg;

  records: AnimalMedicalRecordModel[] = [];
  positions: Record<number, MedicalRecordPositions> = {};
  files: Record<number, MedicalFile[]> = {};
  correctedBy: Record<number, number> = {};
  /** Aktuelle Fassung → frühere Fassungen, jüngste zuerst. */
  history: Record<number, AnimalMedicalRecordModel[]> = {};
  /** Nur aktuelle Fassungen; korrigierte stehen in `history`. */
  currentRecords: AnimalMedicalRecordModel[] = [];
  expandedHistory = new Set<number>();
  /** Fortschreiben-Änderungen je Karteikarte, fertig formatiert. */
  changeChips: Record<number, MedicalChangeChip[]> = {};
  userNames: Record<number, string> = {};
  contactsById: Record<number, ContactModel> = {};
  loaded = false;

  typeFilter: MedicalRecordType | 'all' = 'all';
  availableTypes: MedicalRecordType[] = [];
  first = 0;
  flashRecordId: number | null = null;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _medicalRepository: AnimalMedicalRepository,
    private readonly _contactRepository: ContactRepository
  ) {}

  ngOnInit(): void {
    combineLatest([
      this._medicalRepository.records$,
      this._medicalRepository.positionsByRecordId$,
      this._medicalRepository.filesByRecordId$,
      this._medicalRepository.correctedByMap$,
      this._medicalRepository.correctionHistory$,
      this._medicalRepository.userNames$,
      this._medicalRepository.loaded$,
      this._contactRepository.selectById$(),
    ])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([records, positions, files, correctedBy, history, userNames, loaded, contactsById]) => {
        this.records = records;
        this.positions = positions;
        this.files = files;
        this.correctedBy = correctedBy;
        this.history = history;
        this.currentRecords = records.filter(record => correctedBy[record.id] === undefined);
        this.changeChips = Object.fromEntries(records.map(record => [record.id, toChangeChips(record.changes)]));
        this.userNames = userNames;
        this.loaded = loaded;
        this.contactsById = contactsById;

        const present = new Set(this.currentRecords.map(record => record.type).filter((type): type is MedicalRecordType => !!type));
        this.availableTypes = Object.values(MedicalRecordType).filter(type => present.has(type));
        if (this.typeFilter !== 'all' && !present.has(this.typeFilter)) {
          this.typeFilter = 'all';
        }
        if (this.first >= this.filteredRecords.length) {
          this.first = 0;
        }
        this.applyHighlight();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['highlightRecordId'] && this.highlightRecordId) {
      this.applyHighlight();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get filteredRecords(): AnimalMedicalRecordModel[] {
    return this.typeFilter === 'all' ? this.currentRecords : this.currentRecords.filter(record => record.type === this.typeFilter);
  }

  get pagedRecords(): AnimalMedicalRecordModel[] {
    return this.filteredRecords.slice(this.first, this.first + PAGE_SIZE);
  }

  setTypeFilter(type: MedicalRecordType | 'all'): void {
    this.typeFilter = type;
    this.first = 0;
  }

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
  }

  toggleHistory(headId: number): void {
    if (!this.expandedHistory.delete(headId)) {
      this.expandedHistory.add(headId);
    }
  }

  veterinarianName(record: AnimalMedicalRecordModel): string | null {
    const id = refId(record.veterinarian);
    if (id === null) return null;
    return formatVeterinarianName(this.contactsById[id]) || `#${id}`;
  }

  creatorName(record: AnimalMedicalRecordModel): string {
    const id = refId(record.creator);
    return (id !== null && this.userNames[id]) || '–';
  }

  correctsId(record: AnimalMedicalRecordModel): number | null {
    return refId(record.correctsEntry);
  }

  isCorrected(record: AnimalMedicalRecordModel): boolean {
    return record.corrected || this.correctedBy[record.id] !== undefined;
  }

  treatmentTypes(treatment: AnimalPreventiveTreatmentModel): string[] {
    return treatment.types ?? [];
  }

  nutritionLabel(value: string | null): string | null {
    return NUTRITION_OPTIONS.find(option => option.value === value)?.label ?? null;
  }

  careLabel(value: string): string {
    return CARE_OPTIONS.find(option => option.value === value)?.label ?? value;
  }

  typeSeverity(type: MedicalRecordType | null): 'info' | 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (type) {
      case MedicalRecordType.EXAMINATION: return 'info';
      case MedicalRecordType.TREATMENT:
      case MedicalRecordType.VACCINATION: return 'success';
      case MedicalRecordType.SURGERY: return 'warn';
      case MedicalRecordType.EUTHANASIA: return 'contrast';
      default: return 'secondary';
    }
  }

  fileIcon(file: MedicalFile): string {
    if (file.kind === 'image') return 'pi pi-image';
    if (file.kind === 'video') return 'pi pi-video';
    return file.name.toLowerCase().endsWith('.pdf') ? 'pi pi-file-pdf' : 'pi pi-file';
  }

  /** Springt auf die Seite mit dem Eintrag und hebt ihn kurz hervor; frühere Fassungen werden aufgeklappt. */
  private applyHighlight(): void {
    const id = this.highlightRecordId;
    if (!id || !this.records.length) return;
    const headId = this.headIdOf(id);
    const index = this.currentRecords.findIndex(record => record.id === headId);
    if (index < 0) return;

    if (headId !== id) {
      this.expandedHistory.add(headId);
    }
    this.typeFilter = 'all';
    this.first = Math.floor(index / PAGE_SIZE) * PAGE_SIZE;
    this.flashRecordId = id;
    this.highlightRecordId = null;
    setTimeout(() => document.getElementById(`medical-record-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    setTimeout(() => this.flashRecordId = null, 2500);
  }

  private headIdOf(id: number): number {
    let headId = id;
    const seen = new Set<number>([headId]);
    while (this.correctedBy[headId] !== undefined && !seen.has(this.correctedBy[headId])) {
      headId = this.correctedBy[headId];
      seen.add(headId);
    }
    return headId;
  }
}
